import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Icons
import { 
  Users, 
  UserPlus, 
  UserMinus, 
  Search, 
  Plus,
  Edit,
  Trash2,
  Link,
  Unlink,
  GraduationCap,
  User,
  Mail,
  Phone,
  Calendar,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentParentManagement = ({ user }) => {
  const [students, setStudents] = useState([]);
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedParent, setSelectedParent] = useState(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [createStudentDialogOpen, setCreateStudentDialogOpen] = useState(false);
  
  // Form states
  const [studentForm, setStudentForm] = useState({
    user_id: '',
    class_id: '',
    student_number: '',
    birth_date: '',
    parent_ids: [],
    medical_info: ''
  });

  useEffect(() => {
    const allowedRoles = ['ADMIN', 'PRINCIPAL', 'SECRETARY'];
    if (allowedRoles.includes(user.role)) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch students and parents in parallel
      const [studentsResponse, parentsResponse] = await Promise.all([
        axios.get(`${API}/students`),
        axios.get(`${API}/users?role=PARENT`)
      ]);

      setStudents(studentsResponse.data);
      setParents(parentsResponse.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkParentToStudent = async (studentId, parentId) => {
    try {
      await axios.post(`${API}/students/${studentId}/parents`, {
        parent_id: parentId,
        student_id: studentId
      });

      // Update local state
      setStudents(prev => prev.map(student => {
        if (student.id === studentId) {
          const updatedParentIds = [...(student.parent_ids || [])];
          if (!updatedParentIds.includes(parentId)) {
            updatedParentIds.push(parentId);
          }
          return { ...student, parent_ids: updatedParentIds };
        }
        return student;
      }));

      toast.success('Parent lié avec succès à l\'étudiant');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la liaison';
      toast.error(errorMsg);
      console.error('Error linking parent:', error);
    }
  };

  const handleUnlinkParentFromStudent = async (studentId, parentId) => {
    try {
      await axios.delete(`${API}/students/${studentId}/parents/${parentId}`);

      // Update local state
      setStudents(prev => prev.map(student => {
        if (student.id === studentId) {
          const updatedParentIds = (student.parent_ids || []).filter(id => id !== parentId);
          return { ...student, parent_ids: updatedParentIds };
        }
        return student;
      }));

      toast.success('Parent délié avec succès de l\'étudiant');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la déliaison';
      toast.error(errorMsg);
      console.error('Error unlinking parent:', error);
    }
  };

  const handleCreateStudent = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/students`, {
        ...studentForm,
        birth_date: new Date(studentForm.birth_date).toISOString()
      });

      setStudents(prev => [...prev, response.data]);
      toast.success('Étudiant créé avec succès');
      resetStudentForm();
      setCreateStudentDialogOpen(false);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la création';
      toast.error(errorMsg);
      console.error('Error creating student:', error);
    }
  };

  const resetStudentForm = () => {
    setStudentForm({
      user_id: '',
      class_id: '',
      student_number: '',
      birth_date: '',
      parent_ids: [],
      medical_info: ''
    });
  };

  const getParentName = (parentId) => {
    const parent = parents.find(p => p.id === parentId);
    return parent ? `${parent.first_name} ${parent.last_name}` : 'Parent inconnu';
  };

  const getParentEmail = (parentId) => {
    const parent = parents.find(p => p.id === parentId);
    return parent ? parent.email : '';
  };

  const filteredStudents = students.filter(student => {
    const searchLower = searchTerm.toLowerCase();
    const studentNumber = student.student_number?.toLowerCase() || '';
    const className = student.class_id?.toLowerCase() || '';
    return studentNumber.includes(searchLower) || className.includes(searchLower);
  });

  const filteredParents = parents.filter(parent => {
    const searchLower = searchTerm.toLowerCase();
    const fullName = `${parent.first_name} ${parent.last_name}`.toLowerCase();
    const email = parent.email?.toLowerCase() || '';
    return fullName.includes(searchLower) || email.includes(searchLower);
  });

  // Autoriser ADMIN, PRINCIPAL, et SECRETARY
  const allowedRoles = ['ADMIN', 'PRINCIPAL', 'SECRETARY'];
  
  if (!allowedRoles.includes(user.role)) {
    return (
      <div className="p-6">
        <Card className="card">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertTriangle className="w-16 h-16 mx-auto text-red-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Accès refusé</h3>
              <p className="text-gray-600">
                Vous n'avez pas les permissions nécessaires pour accéder à cette section.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="h-64 bg-gray-200 rounded" />
            <div className="h-64 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 fade-in">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <Link className="w-6 h-6 mr-2 text-blue-600" />
            Gestion des Relations Parent-Enfant
          </h1>
          <p className="text-gray-600 mt-1">
            Liez les étudiants à leurs parents pour faciliter les notifications et le suivi
          </p>
        </div>

        <Dialog open={createStudentDialogOpen} onOpenChange={setCreateStudentDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Nouvel étudiant
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Créer un nouvel étudiant</DialogTitle>
              <DialogDescription>
                Ajoutez un nouvel étudiant au système
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateStudent} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="user_id">ID Utilisateur *</Label>
                <Input
                  id="user_id"
                  value={studentForm.user_id}
                  onChange={(e) => setStudentForm({ ...studentForm, user_id: e.target.value })}
                  placeholder="ID de l'utilisateur étudiant"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="class_id">Classe *</Label>
                <Input
                  id="class_id"
                  value={studentForm.class_id}
                  onChange={(e) => setStudentForm({ ...studentForm, class_id: e.target.value })}
                  placeholder="Identifiant de la classe"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="student_number">Numéro étudiant *</Label>
                <Input
                  id="student_number"
                  value={studentForm.student_number}
                  onChange={(e) => setStudentForm({ ...studentForm, student_number: e.target.value })}
                  placeholder="Numéro unique de l'étudiant"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="birth_date">Date de naissance *</Label>
                <Input
                  id="birth_date"
                  type="date"
                  value={studentForm.birth_date}
                  onChange={(e) => setStudentForm({ ...studentForm, birth_date: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="medical_info">Informations médicales</Label>
                <Input
                  id="medical_info"
                  value={studentForm.medical_info}
                  onChange={(e) => setStudentForm({ ...studentForm, medical_info: e.target.value })}
                  placeholder="Allergies, conditions médicales, etc."
                />
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    resetStudentForm();
                    setCreateStudentDialogOpen(false);
                  }}
                >
                  Annuler
                </Button>
                <Button type="submit" className="btn-primary">
                  Créer l'étudiant
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <GraduationCap className="w-4 h-4 mr-2 text-blue-600" />
              Total Étudiants
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{students.length}</div>
          </CardContent>
        </Card>

        <Card className="card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <Users className="w-4 h-4 mr-2 text-green-600" />
              Total Parents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{parents.length}</div>
          </CardContent>
        </Card>

        <Card className="card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <Link className="w-4 h-4 mr-2 text-purple-600" />
              Relations établies
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {students.filter(s => s.parent_ids && s.parent_ids.length > 0).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Rechercher étudiants ou parents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Content Tabs */}
      <Tabs defaultValue="students" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="students">Étudiants ({students.length})</TabsTrigger>
          <TabsTrigger value="parents">Parents ({parents.length})</TabsTrigger>
        </TabsList>

        {/* Students Tab */}
        <TabsContent value="students" className="space-y-4">
          {filteredStudents.length > 0 ? (
            <div className="grid grid-cols-1 gap-4">
              {filteredStudents.map((student) => (
                <Card key={student.id} className="card">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-3">
                          <GraduationCap className="w-5 h-5 text-blue-600" />
                          <h3 className="font-semibold text-lg">{student.first_name} {student.last_name}</h3>
                          <Badge variant="outline">N° {student.student_number}</Badge>
                          <Badge variant="outline">Classe: {student.class_id}</Badge>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <p className="text-sm text-gray-600">Email:</p>
                            <p className="font-medium">{student.email}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Date de naissance:</p>
                            <p className="font-medium">{new Date(student.birth_date).toLocaleDateString('fr-FR')}</p>
                          </div>
                          {student.medical_info && (
                            <div>
                              <p className="text-sm text-gray-600">Info médicale:</p>
                              <p className="font-medium">{student.medical_info}</p>
                            </div>
                          )}
                        </div>

                        {/* Parents liés */}
                        <div className="mb-4">
                          <p className="text-sm text-gray-600 mb-2">Parents liés:</p>
                          {student.parent_ids && student.parent_ids.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {student.parent_ids.map((parentId) => (
                                <div key={parentId} className="flex items-center bg-green-50 px-3 py-1 rounded-full">
                                  <span className="text-sm font-medium text-green-800">
                                    {getParentName(parentId)}
                                  </span>
                                  <span className="text-xs text-green-600 ml-2">
                                    ({getParentEmail(parentId)})
                                  </span>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleUnlinkParentFromStudent(student.id, parentId)}
                                    className="ml-2 h-6 w-6 p-0 text-red-600 hover:text-red-800"
                                  >
                                    <Unlink className="w-3 h-3" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <Badge variant="outline" className="text-orange-600">
                              Aucun parent lié
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="ml-4">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button size="sm" className="btn-primary">
                              <UserPlus className="w-4 h-4 mr-2" />
                              Lier parent
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Lier un parent à l'étudiant</DialogTitle>
                              <DialogDescription>
                                Sélectionnez un parent à lier à {student.first_name} {student.last_name} (N° {student.student_number})
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 max-h-96 overflow-y-auto">
                              {parents.filter(parent => 
                                !student.parent_ids?.includes(parent.id)
                              ).map((parent) => (
                                <div key={parent.id} className="flex items-center justify-between p-3 border rounded-lg">
                                  <div>
                                    <p className="font-medium">{parent.first_name} {parent.last_name}</p>
                                    <p className="text-sm text-gray-600">{parent.email}</p>
                                  </div>
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      handleLinkParentToStudent(student.id, parent.id);
                                    }}
                                    className="btn-primary"
                                  >
                                    <Link className="w-4 h-4 mr-1" />
                                    Lier
                                  </Button>
                                </div>
                              ))}
                              {parents.filter(parent => 
                                !student.parent_ids?.includes(parent.id)
                              ).length === 0 && (
                                <p className="text-center text-gray-500 py-4">
                                  Tous les parents disponibles sont déjà liés
                                </p>
                              )}
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <GraduationCap className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun étudiant trouvé</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm ? 'Aucun étudiant ne correspond à votre recherche.' : 'Aucun étudiant dans le système.'}
              </p>
            </div>
          )}
        </TabsContent>

        {/* Parents Tab */}
        <TabsContent value="parents" className="space-y-4">
          {filteredParents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredParents.map((parent) => {
                const linkedStudents = students.filter(student => 
                  student.parent_ids?.includes(parent.id)
                );
                
                return (
                  <Card key={parent.id} className="card">
                    <CardContent className="pt-6">
                      <div className="flex items-start space-x-3">
                        <User className="w-5 h-5 text-green-600 mt-1" />
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{parent.first_name} {parent.last_name}</h3>
                          <p className="text-sm text-gray-600 mb-3">{parent.email}</p>
                          
                          <div>
                            <p className="text-sm text-gray-600 mb-2">Enfants:</p>
                            {linkedStudents.length > 0 ? (
                              <div className="space-y-2">
                                {linkedStudents.map((student) => (
                                  <div key={student.id} className="flex items-center bg-blue-50 px-3 py-2 rounded">
                                    <GraduationCap className="w-4 h-4 text-blue-600 mr-2" />
                                    <span className="text-sm font-medium text-blue-800">
                                      N° {student.student_number} - Classe {student.class_id}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <Badge variant="outline" className="text-orange-600">
                                Aucun enfant lié
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun parent trouvé</h3>
              <p className="text-gray-600">
                {searchTerm ? 'Aucun parent ne correspond à votre recherche.' : 'Aucun parent dans le système.'}
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StudentParentManagement;